import React, { useState, useCallback, useRef, useEffect } from 'react';
import BaseModal from '../modals/BaseModal';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Cropper from 'react-easy-crop';
import Slider from '@mui/material/Slider';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../resources/strings/StringKeys';
import { getCroppedImg } from './GetCroppedImage';
import { useToast, ToastType } from '../../context/toast/ToastContext';
import { ImageUploadType } from './ImageUploadType';
import { uploadAssetImageAndReferenceInFirestore, uploadInventoryImageAndReferenceInFirestore } from '../../repository/DocumentsDAO';
import { IconButton, Stack, Box, useMediaQuery, useTheme, CircularProgress } from '@mui/material';
import { IconMinus, IconPlus, IconUpload, IconX } from '@tabler/icons-react';
import { updateProfilePicture } from '../../repository/UserDAO';
import { useLoadingContext } from '../../context/loading/LoadingContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useOrganizationContext } from '../../context/organization_context/OrganizationContext';
import { uploadProductImageAndReferenceInFirestore } from '../../repository/ProductsDAO';
import { CloudFunctions } from '../../utils/enums/CloudFunctions';
import { updateOrganizationLogo } from '../../repository/OrganizationDAO';

const ImageUploadModal = ({ show, onHide, modalTitle, cropShape = 'square', organization, itemId, uploadType }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [imagePreview, setImagePreview] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [isDragging, setIsDragging] = useState(false);
    const { setLoadingData } = useLoadingContext();
    const { organizationStripeInfo } = useOrganizationContext();
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [imageLoadError, setImageLoadError] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    // Reset state when modal is closed
    useEffect(() => {
        if (!show) {
            resetModal();
        }
    }, [show]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            validateAndSetImage(file);
        }
    };

    const validateAndSetImage = (file) => {
        console.log("validateAndSetImage called with file:", file.name, "type:", file.type, "size:", file.size);
        
        if (!file.type.startsWith('image/')) {
            console.error("File validation failed: Not an image file");
            showToast(t(StringKeys.PLEASE_SELECT_A_FILE), ToastType.DANGER);
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            console.error("File validation failed: File too large", file.size);
            showToast(t(StringKeys.FILE_TOO_LARGE), ToastType.DANGER);
            return;
        }

        // Revoke previous object URL to prevent memory leaks
        if (imagePreview) {
            console.log("Revoking previous object URL");
            URL.revokeObjectURL(imagePreview);
        }

        setImageLoadError(false);
        setImageLoading(true);
        console.log("Starting file read process");
        
        // For Android with large files, use a different approach
        if (isAndroid && file.size > 1.5 * 1024 * 1024) { // Over 1.5MB
            console.log("Large file on Android detected, using chunked processing");
            processLargeImageFile(file);
            return;
        }
        
        // For Android compatibility, read the file first
        const reader = new FileReader();
        
        // Set a timeout for the FileReader operation
        const readerTimeout = setTimeout(() => {
            console.warn("FileReader operation taking too long, trying alternative approach");
            tryDirectObjectURL();
        }, 3000); // 3 seconds timeout
        
        // Alternative approach using direct object URL (works better on some Android devices)
        const tryDirectObjectURL = () => {
            try {
                console.log("Trying direct Object URL approach");
                const objectUrl = URL.createObjectURL(file);
                
                const directImg = new Image();
                directImg.onload = () => {
                    console.log("Direct Object URL image loaded successfully");
                    clearTimeout(readerTimeout);
                    setImageLoading(false);
                    setSelectedFile(file);
                    setFileName(file.name);
                    setImagePreview(objectUrl);
                };
                
                directImg.onerror = (error) => {
                    console.error("Direct Object URL approach failed:", error);
                    // If both approaches fail, show error
                    if (!imagePreview) {
                        setImageLoading(false);
                        setImageLoadError(true);
                        showToast(t(StringKeys.IMAGE_LOAD_ERROR), ToastType.DANGER);
                    }
                };
                
                directImg.src = objectUrl;
            } catch (error) {
                console.error("Error in direct Object URL approach:", error);
            }
        };
        
        reader.onload = (e) => {
            console.log("FileReader onload triggered, result type:", typeof e.target.result);
            clearTimeout(readerTimeout);
            
            // Create a new image to test loading
            const img = new Image();
            
            img.onload = () => {
                console.log("Image loaded successfully, dimensions:", img.width, "x", img.height);
                setImageLoading(false);
                // If image loads successfully, set the file and preview
                setSelectedFile(file);
                setFileName(file.name);
                // Use the FileReader result for more reliable loading on Android
                setImagePreview(e.target.result);
            };
            
            img.onerror = (error) => {
                console.error("Image load error:", error);
                console.error("Image source length:", e.target.result ? e.target.result.length : 'null');
                console.error("Image source starts with:", e.target.result ? e.target.result.substring(0, 50) + '...' : 'null');
                
                // Try the direct Object URL approach as fallback
                tryDirectObjectURL();
            };
            
            console.log("Setting image source from FileReader result");
            img.src = e.target.result;
        };
        
        reader.onerror = (error) => {
            console.error("FileReader error:", error);
            console.error("FileReader error details:", reader.error);
            clearTimeout(readerTimeout);
            
            // Try the direct Object URL approach as fallback
            tryDirectObjectURL();
        };
        
        console.log("Starting readAsDataURL");
        reader.readAsDataURL(file);
    };

    // Special handling for large files on Android
    const processLargeImageFile = (file) => {
        console.log("Processing large image file with special handling");
        try {
            // Create a direct object URL - more memory efficient for large files
            const objectUrl = URL.createObjectURL(file);
            
            // Set a placeholder while we process
            setSelectedFile(file);
            setFileName(file.name);
            setImagePreview(objectUrl);
            setImageLoading(false);
            
            console.log("Large file processed with object URL");
        } catch (error) {
            console.error("Error processing large file:", error);
            setImageLoading(false);
            setImageLoadError(true);
            showToast(t(StringKeys.IMAGE_LOAD_ERROR), ToastType.DANGER);
        }
    };

    // Mobile-friendly approach - trigger file input click
    const handleSelectClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // For desktop browsers that support drag and drop
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files[0];
        if (file) {
            validateAndSetImage(file);
        }
        setIsDragging(false);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        console.log("Crop completed:", croppedAreaPixels);
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const onMediaLoaded = useCallback((mediaSize) => {
        console.log("Media loaded, size:", mediaSize);
        const { width, height } = mediaSize;
        // Always use 1:1 aspect ratio for square crop
        const aspectRatio = cropShape === 'rect' ? 4 / 3 : 1;
        const minZoom = Math.max(1, Math.max(width, height) / Math.min(width, height));
        console.log("Setting initial zoom to:", minZoom);
        setZoom(minZoom);
    }, [cropShape]);

    const handleUpload = async () => {
        if (!selectedFile) {
            showToast(t(StringKeys.PLEASE_SELECT_A_FILE), ToastType.WARNING);
            return;
        }

        if (!croppedAreaPixels) {
            showToast(t(StringKeys.PLEASE_CROP_THE_IMAGE), ToastType.WARNING);
            return;
        }

        setIsUploading(true);
        setLoadingData(true);
        
        try {
            console.log("Starting image crop with pixels:", croppedAreaPixels);
            const croppedImage = await getCroppedImg(imagePreview, croppedAreaPixels);
            console.log("Image cropped successfully, size:", croppedImage.size);
            let result;
        
            switch (uploadType) {
                case ImageUploadType.InventoryImage:
                    result = await uploadInventoryImageAndReferenceInFirestore(organization, itemId, croppedImage);
                    break;
                case ImageUploadType.AssetImage:
                    result = await uploadAssetImageAndReferenceInFirestore(organization, itemId, croppedImage);
                    break;
                case ImageUploadType.EmployeeImage:
                    result = await updateProfilePicture(organization, itemId, croppedImage);
                    break;
                case ImageUploadType.OrganizationImage:
                    try {
                        result = await updateOrganizationLogo(organization, croppedImage);
                        if (result.success) {
                            showToast(t(StringKeys.SUCCESS));
                            onHide();
                            resetModal();
                        } else {
                            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                            console.error("Error updating organization logo:", result.error);
                        }
                    } catch(e) {
                        showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                        console.error("Error updating organization logo:", e);
                    } finally {
                        setLoadingData(false);
                        setIsUploading(false);
                        return;
                    }
                    break;
                case ImageUploadType.ProductImage:
                    try {
                        const url = await uploadProductImageAndReferenceInFirestore(organization, itemId, croppedImage);
                        
                        if (url) {
                            const functions = getFunctions();
                            const updateProductImage = httpsCallable(functions, CloudFunctions.UpdateProductImage);
                            
                            const response = await updateProductImage({
                                accountId: organizationStripeInfo.stripe_account_id,
                                productId: itemId,
                                url: url
                            });
        
                            if (response.data.success) {
                                showToast(t(StringKeys.SUCCESS));
                            } else {
                                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                                console.error(response.data.error);
                            }
                        } else {
                            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                        }
                    } catch (error) {
                        console.error('Error uploading and updating product image:', error);
                        showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                    } finally {
                        setLoadingData(false);
                        setIsUploading(false);
                        onHide();
                        resetModal();
                        return;
                    }
                    break;
                default:
                    console.error('Unknown upload type');
                    setLoadingData(false);
                    setIsUploading(false);
                    return;
            }
        
            if (result && result.success) {
                showToast(t(StringKeys.SUCCESS));
            } else if (result) {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
        } catch (error) {
            console.error(`Error during ${uploadType} upload:`, error);
            console.error("Error stack:", error.stack);
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            setIsUploading(false);
            onHide();
            resetModal();
        }
    };  

    const resetModal = () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        setSelectedFile(null);
        setFileName("");
        setImagePreview(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setIsDragging(false);
    };

    const handleRemoveImage = () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        setSelectedFile(null);
        setFileName("");
        setImagePreview(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const actions = (
        <>
            <Button 
                onClick={handleUpload} 
                disabled={!selectedFile || isUploading}
                variant="contained"
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ 
                    minWidth: '120px',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' }
                }}
            >
                {isUploading ? t(StringKeys.UPLOADING) : t(StringKeys.UPLOAD_IMAGE)}
            </Button>
        </>
    );

    return (
        <BaseModal
            open={show}
            onClose={() => {
                if (!isUploading) {
                    handleRemoveImage();
                    onHide();
                }
            }}
            title={modalTitle}
            actions={actions}
        >
            <Box sx={{ 
                width: isMobile ? '100%' : '650px',
                maxWidth: '100%',
                margin: '0 auto',
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2 
            }}>                
            {!imagePreview ? (
                <Box
                    onClick={handleSelectClick}
                    onDrop={!isMobile ? handleDrop : undefined}
                    onDragOver={!isMobile ? handleDragOver : undefined}
                    onDragLeave={!isMobile ? handleDragLeave : undefined}
                    sx={{
                        paddingTop: isMobile ? '75%' : '100%',
                        position: 'relative',
                        border: isDragging ? '2px dashed #1976d2' : '2px dashed #ccc',
                        borderRadius: 2,
                        backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.04)' : '#f9f9f9',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            borderColor: '#1976d2'
                        }
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={handleFileChange}
                        accept="image/*"
                        capture={isMobile ? "environment" : undefined}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2
                        }}
                    >
                        <IconUpload 
                            size={isMobile ? 36 : 48} 
                            color={isDragging ? "#1976d2" : "#666"} 
                            style={{ transition: 'color 0.3s ease' }}
                        />
                        <Typography 
                            variant="subtitle1" 
                            color="text.secondary"
                            textAlign="center"
                            sx={{ px: 2 }}
                        >
                            {isMobile 
                                ? t(StringKeys.TAP_TO_SELECT_IMAGE)
                                : (isDragging 
                                    ? t(StringKeys.DROP_HERE) 
                                    : t(StringKeys.DRAG_AND_DROP_OR_CLICK_TO_UPLOAD))}
                        </Typography>
                        <Typography 
                            variant="caption" 
                            color="text.secondary"
                        >
                            {t(StringKeys.MAXIMUM_FILE_SIZE_, {size: 5})}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ 
                        position: 'relative',
                        width: '100%',
                        paddingTop: '100%', // Always square aspect ratio
                        backgroundColor: '#f0f0f0',
                        borderRadius: 2,
                        overflow: 'hidden'
                    }}>
                        {imageLoading ? (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CircularProgress />
                            </Box>
                        ) : imageLoadError ? (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 2
                            }}>
                                <Typography color="error" variant="body1" align="center" gutterBottom>
                                    {t(StringKeys.IMAGE_LOAD_ERROR)}
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    color="primary" 
                                    onClick={handleRemoveImage}
                                    startIcon={<IconX size={18} />}
                                >
                                    {t(StringKeys.REMOVE_AND_TRY_AGAIN)}
                                </Button>
                            </Box>
                        ) : (
                            <Box sx={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0
                            }}>
                                <Cropper
                                    image={imagePreview}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={cropShape === 'rect' ? 4 / 3 : 1}
                                    cropShape={cropShape}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    onMediaLoaded={onMediaLoaded}
                                    showGrid={true}
                                    objectFit="contain"
                                    touchDragThreshold={isMobile ? 20 : 0}
                                    crossOrigin="anonymous"
                                />
                            </Box>
                        )}
                        <IconButton
                            onClick={handleRemoveImage}
                            disabled={isUploading}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <IconX color="white" size={20} />
                        </IconButton>
                    </Box>
                    {!imageLoadError && (
                        <Stack 
                            direction="row" 
                            alignItems="center" 
                            spacing={2} 
                            sx={{ mt: 2 }}
                        >
                            <IconButton
                                onClick={() => setZoom(Math.max(zoom - 0.1, 1))}
                                size="small"
                                disabled={isUploading || zoom <= 1 || imageLoading}
                                sx={{ 
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <IconMinus size={20} />
                            </IconButton>
                            <Slider
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.1}
                                aria-labelledby="Zoom"
                                onChange={(_, value) => setZoom(value)}
                                disabled={isUploading || imageLoading}
                                sx={{ flex: 1 }}
                            />
                            <IconButton
                                onClick={() => setZoom(Math.min(zoom + 0.1, 3))}
                                size="small"
                                disabled={isUploading || zoom >= 3 || imageLoading}
                                sx={{ 
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <IconPlus size={20} />
                            </IconButton>
                        </Stack>
                    )}
                    {fileName && (
                        <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                        >
                            {fileName}
                        </Typography>
                    )}
                </Box>
            )}
            </Box>
        </BaseModal>
    );
};

export default ImageUploadModal;